import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Card, Group, Stack, Text } from "@mantine/core";
import { Bell, Hash } from "lucide-react";
import { toast } from "sonner";
import { SlackInstallCard } from "@/components/settings/slack-install-card";
import { useSlackWorkspaces } from "@/hooks/use-slack-workspaces";

/** Why Slack's install flow sent the member back (`?error=` from the OAuth
 *  callback in `backend/slack/api/management.py`). */
const SLACK_ERRORS: Record<string, string> = {
  already_connected: "Your company already has a Slack workspace connected.",
  workspace_in_use: "That Slack workspace is already connected to Nodus elsewhere (for the whole site or another company).",
};

/** Slack channels are notification *targets* now (#268) — a tenant admin adds
 *  them on the Notifications page alongside shared mailboxes and phone
 *  numbers, so this card only points the way. */
function SlackNotificationChannelsHint() {
  return (
    <Card withBorder padding="lg">
      <Group gap="xs" mb="xs">
        <Hash size={16} style={{ color: "var(--mantine-color-dimmed)" }} />
        <Text fw={500}>Notification channels</Text>
      </Group>
      <Text fz="xs" c="dimmed" mb="md">
        Choose which Slack channels get notified — and what they get notified about — under
        shared destinations on the Notifications page.
      </Text>
      <Button variant="light" component={Link} to="/notifications">
        <Bell size={16} style={{ marginRight: 8 }} />
        Open notification settings
      </Button>
    </Card>
  );
}

/** Settings → Slack: connect the workspace, how members link, where channels are chosen.
 *  Also where Slack's install flow lands (`?installed=true` / `?error=…`): say how it
 *  went once, then drop the parameters so a reload doesn't say it again. */
export function SlackSettingsSection() {
  const { data: workspaces = [] } = useSlackWorkspaces();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const error = searchParams.get("error");
    const installed = searchParams.get("installed");
    if (!error && !installed) return;
    // A small delay so the Toaster has mounted (as on the Account page).
    const timer = setTimeout(() => {
      if (installed) toast.success("Slack connected");
      if (error) toast.error(SLACK_ERRORS[error] ?? "Couldn't connect Slack. Please try again.");
      const next = new URLSearchParams(searchParams);
      next.delete("error");
      next.delete("installed");
      next.delete("team");
      setSearchParams(next, { replace: true });
    }, 100);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack gap="sm">
      <SlackInstallCard />
      <Card withBorder padding="md">
        <Text fz="xs" c="dimmed">
          After installing, team members can use <code>/link</code> in Slack to connect their accounts,
          or link from their profile page.
        </Text>
      </Card>
      {workspaces.length > 0 && <SlackNotificationChannelsHint />}
    </Stack>
  );
}
